import { useAtom, useAtomValue } from "jotai";
import {
  convertTopicsToParams,
  interestedTopicsAtom,
  selectedStyleAtom,
  tabsApiAtom,
} from "../constants/budget-atom";
import {
  kInterestedTopics,
  kSelectionLimit,
} from "../constants/budget-constants";
import BudgetPaginationButton from "../ui/budget-pagination-button";
import { useMemo } from "react";
import { kBudgetConfig } from "../constants/budget-translations";
import { useRouter } from "next/router";

const BudgetTopicPill: React.FC<{
  topicKey: string;
  label: string;
  emoji: string;
  onClick: (number) => void;
  isSelected: boolean;
}> = ({ topicKey, label, emoji, onClick, isSelected }) => {
  return (
    <button
      onClick={() => onClick(topicKey)}
      className={`mb-2 mr-2 flex flex-row items-center space-x-1 rounded-xl px-4 py-2 transition-all duration-300 ease-in-out
        ${
          isSelected
            ? "bg-secondary text-text-darkest"
            : "bg-off-white text-text-dark"
        }`}
    >
      <span className="font-poppins text-sm">
        {emoji} {label}
      </span>
    </button>
  );
};

const BudgetTopicSelection: React.FC = () => {
  const [selectedStyle] = useAtom(selectedStyleAtom);
  const [interestedTopics, setInterestedTopics] = useAtom(interestedTopicsAtom);
  const tabsApi = useAtomValue(tabsApiAtom);
  const copies = useMemo(() => kBudgetConfig(selectedStyle), [selectedStyle]);
  const router = useRouter();

  /// Handles topic selection, and limits selectable items to 5
  /// Subsequently updates the query params when the topics are selected
  function onTopicSelected(topicKey: string) {
    var updatedTopics;
    if (interestedTopics.includes(topicKey)) {
      updatedTopics = interestedTopics.filter(
        (topicId) => topicId !== topicKey
      );
    } else {
      if (interestedTopics.length == kSelectionLimit) {
        return;
      }
      updatedTopics = [...interestedTopics, topicKey];
    }

    setInterestedTopics(updatedTopics);
  }

  function addTopicsToParams() {
    const topicParams = convertTopicsToParams(interestedTopics);
    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, topic: topicParams.map(String) },
      },
      undefined,
      { shallow: true }
    );
  }

  return (
    <div className="flex flex-col items-center space-y-4 px-8 py-6">
      <div className="font-poppins text-2xl font-bold">
        {selectedStyle && copies.kInterestedTopicsTitle}
      </div>
      <div className="font-lg font-poppins text-sm text-text-dark">
        {selectedStyle && copies.kInterestedTopicsSubtitle}
      </div>
      <div className="flex flex-wrap justify-center">
        {Object.entries(kInterestedTopics).map(([key, { title, emoji }]) => (
          <BudgetTopicPill
            key={key}
            topicKey={key}
            emoji={emoji}
            label={title[selectedStyle]}
            onClick={onTopicSelected}
            isSelected={interestedTopics.includes(key)}
          />
        ))}
      </div>
      <BudgetPaginationButton
        placeholderText={copies.kInterestedTopicsButton}
        activeText={copies.kNextButton}
        onClick={() => {
          if (interestedTopics.length > 0) {
            tabsApi.scrollNext();
            addTopicsToParams();
          }
        }}
        showActiveText={interestedTopics.length > 0}
      />
    </div>
  );
};

export default BudgetTopicSelection;
